import { useMemo } from 'react';

import { useFlashAvailabilities } from '~/hooks/apis/calendar/useFlashAvailability';
import { useGetTourOfferDepartures } from '~/hooks/apis/tours/useTourOffers';

import { Bundle, HotelBundleComponent } from '~/types/bundles/bundle';

import { checkInDate, hotelStatus } from './hotelStay';
import { isTourAvailable } from './tourAvailability';
import useBundleTourSpan from './useBundleTourSpan';

/** Each departure the bundle sells, checked against the tour's spaces and every hotel's real check-in. */
export default function useBundleDepartureChecks(bundle: Bundle) {
  const hotels = bundle.components.filter(
    (component): component is HotelBundleComponent => component.product_type === 'flashAccommodation',
  );
  const departures = useMemo(() => [...bundle.departure_dates].sort(), [bundle.departure_dates]);

  const { tour } = useBundleTourSpan(bundle);
  const {
    data: tourDepartures = [],
    isFetching: isCheckingTour,
    error: tourError,
  } = useGetTourOfferDepartures(tour?.tour_offer_id);
  const tourDepartureOn = (date: string) => tourDepartures.find((departure) => departure.startDate === date);

  const hotelResults = useFlashAvailabilities(
    hotels.map((hotel) => ({
      stay: { offerId: hotel.offer_id, packageId: hotel.package_id, nights: hotel.nights },
      checkInDates: departures.map((date) => checkInDate(date, hotel.day_offset ?? 0)),
    })),
  );

  const isChecking = isCheckingTour || hotelResults.some((result) => result.isFetching);
  const hotelError = hotelResults.find((result) => result.error)?.error;
  // A check that failed is unknown, not unsellable; tourError and hotelError report it instead.
  const blocked = isChecking
    ? []
    : departures.filter(
        (date) =>
          (!tourError && !isTourAvailable(tourDepartureOn(date))) ||
          hotels.some(
            (hotel, index) =>
              !hotelResults[index].error &&
              hotelStatus(hotelResults[index].data, checkInDate(date, hotel.day_offset ?? 0)) !== 'available',
          ),
      );

  const checkError = tourError ?? hotelError;

  return {
    tour,
    hotels,
    departures,
    tourDepartureOn,
    hotelResults,
    isChecking,
    tourError,
    hotelError,
    checkError,
    blocked,
  };
}
